import React, { useEffect, useState, useMemo } from "react";
import styles from "./styles.css";
import { NUMBER } from "../../../constants/app-constants";
import PropTypes from "prop-types";
import { STEP_NAMES } from "../vehicle-information/constants";
import PopularBrands from "../popular-brands";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { SearchInput } from "@cars24/turbo-web/lib/search-input";
import { Label } from "@cars24/turbo-web/lib/label";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { updateDisplayNameToLabel } from "./selector";
import IconSpinner from "../../shared/icon-spinner";

const BrandList = ({
    brands,
    fetchBrandsConnect,
    setDetailsConnect,
    brand,
    customCallback = () => {},
    isVhrFlow = false
}) => {
    const { tokens } = useTokens();
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const { brand: selectedBrandData  } = brand || {};
    const { id: selectedBrandId } = selectedBrandData || {};

    useEffect(() => {
        setIsLoading(true);
        fetchBrandsConnect()
            .then(() => {
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [fetchBrandsConnect]);

    const handleOnChange = (searchQuery) => {
        setSearchText(searchQuery);
    };

    const handleSelectBrand = async({ name, id, makeDisplay }) => {
        await yieldToMain();
        setDetailsConnect({
            [STEP_NAMES.Brand]: { name, id },
            ...(selectedBrandId !== id && {[STEP_NAMES.Model]: {}}),
            activeAccordionStepIndex: isVhrFlow ? NUMBER.ZERO : NUMBER.ONE,
            ownerName: ""
        });

        customCallback({id, makeDisplay});
    };

    const handleCallback = async(selectedBrand) => {
        await yieldToMain();
        handleSelectBrand({
            name: selectedBrand.displayText,
            id: selectedBrand.id,
            makeDisplay: selectedBrand.make_display
        });
    };

    const handleSelectBrandClick = async (brandData) => {
        await yieldToMain();
        handleSelectBrand({
            name: brandData.label,
            id: brandData.make_id,
            makeDisplay: brandData.make_display
        });
    };

    const filteredBrands = useMemo(() => {
        if (!searchText) {
            return brands;
        }
        return brands.filter((item) =>
            item.displayName && item.displayName.toLowerCase().includes(searchText.toLowerCase())
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, brands]);

    const popularBrands = useMemo(
        () => filteredBrands?.filter((item) => item.is_popular === "yes" && item.logo),
        [filteredBrands]
    );

    const filteredBrandsList = useMemo(() => {
        return updateDisplayNameToLabel(filteredBrands);
    }, [filteredBrands]);

    if (isLoading) return <div styleName={"styles.loadingIcon"}><IconSpinner/></div>;

    return (
        <div>
            <div styleName={"styles.searchField"}>
                <SearchInput placeholder="Select car brand" value={searchText}  onChange={(e) => handleOnChange(e.target.value)} showCross={false} />
            </div>
            {((filteredBrands && filteredBrands.length) || (popularBrands && popularBrands.length)) ?
                <React.Fragment>
                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">Popular Brands</Label>
                    <PopularBrands
                        selected={selectedBrandId}
                        callback={handleCallback}
                        popularBrands={popularBrands}
                        isRevamped
                    />

                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">All Brands</Label>
                    <ul styleName={"styles.brandsList styles.allModels"}>
                        {filteredBrandsList?.map((item, i) => (
                            <li
                                onClick={() => handleSelectBrandClick(item)}
                                styleName={item.id === selectedBrandId ? "styles.selected_state" : ""}
                                key={i}
                            >
                                <Label level={3}>{item.label}</Label>
                            </li>
                        ))}
                    </ul>
                </React.Fragment>
                :
                <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">No results found</Label>
            }
        </div>
    );
};

export default BrandList;

BrandList.propTypes = {
    brands: PropTypes.array,
    fetchBrandsConnect: PropTypes.func,
    setDetailsConnect: PropTypes.func,
    brand: PropTypes.object,
    setActiveAccordionStepIndexConnect: PropTypes.func,
    customCallback: PropTypes.func,
    isVhrFlow: PropTypes.bool
};
